import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Loader2, FolderPlus, ExternalLink, Settings, Building2, Plug, X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { gdriveAPI } from '../lib/api';
import { useAuthStore } from '../store';

function ConfigDialog({ open, onOpenChange, status, onSaved }) {
  const [root, setRoot] = useState('');
  const [subs, setSubs] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && status) { setRoot(status.root_folder_url || ''); setSubs(status.subfolders || []); } }, [open, status]);
  const save = async () => {
    setBusy(true);
    try { await gdriveAPI.saveConfig(root, subs); toast.success('Configuración de Drive guardada'); onSaved(); onOpenChange(false); }
    catch (e) { toast.error(e?.message || 'Error al guardar'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="gdrive-config-dialog">
        <DialogHeader><DialogTitle>Configurar carpetas de Karma Digital</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-xs font-medium mb-1">Carpeta general de clientes (enlace o ID)</p>
            <Input value={root} onChange={(e) => setRoot(e.target.value)} data-testid="gdrive-config-root" />
          </div>
          <div className="space-y-2">
            <p className="text-xs font-medium">Subcarpetas que se crean dentro de cada cliente</p>
            {subs.map((s, i) => (
              <div key={i} className="flex gap-2">
                <Input value={s} onChange={(e) => setSubs(subs.map((x, k) => (k === i ? e.target.value : x)))} data-testid={`gdrive-config-sub-${i}`} />
                <Button variant="ghost" size="icon" onClick={() => setSubs(subs.filter((_, k) => k !== i))} data-testid={`gdrive-config-sub-del-${i}`}><X className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setSubs([...subs, ''])} data-testid="gdrive-config-sub-add"><Plus className="w-4 h-4 mr-1" /> Añadir subcarpeta</Button>
            <p className="text-[11px] text-muted-foreground">Los cambios se aplican a los clientes nuevos. En los existentes, pulsa "Crear / completar carpetas" para añadir las que falten (no se borra nada).</p>
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} data-testid="gdrive-config-save">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Guardar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function OurDriveCard({ clientId, ourDrive, onCreated }) {
  const { user } = useAuthStore();
  const isAdmin = ['ceo', 'manager'].includes(user?.role);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const loadStatus = () => gdriveAPI.status().then(setStatus).catch(() => setStatus({ connected: false }));
  useEffect(() => {
    loadStatus();
    const g = new URLSearchParams(window.location.search).get('gdrive');
    if (g === 'connected') toast.success('Google Drive conectado');
    if (g === 'noscope') toast.error('Falta marcar el permiso de Google Drive en la pantalla de Google. Vuelve a conectar y marca la casilla.');
    if (g === 'error') toast.error('No se pudo conectar Google Drive');
  }, []);

  const connect = async () => {
    try { const r = await gdriveAPI.login(); window.location.href = r.authorization_url; }
    catch (e) { toast.error(e?.message || 'No se pudo iniciar la conexión'); }
  };
  const create = async () => {
    if (!window.confirm('Se creará una carpeta nueva con el nombre del cliente en vuestra carpeta general (si ya existe una con el mismo nombre, se reutiliza). ¿Continuar?')) return;
    setBusy(true);
    try {
      const t = await gdriveAPI.createForClient(clientId);
      toast.success(t.reused ? 'Carpeta existente vinculada y subcarpetas completadas' : 'Carpetas creadas en Google Drive');
      onCreated?.(t);
    } catch (e) { toast.error(e?.message || 'Error creando las carpetas'); }
    finally { setBusy(false); }
  };
  const linkExisting = async () => {
    setBusy(true);
    try {
      const t = await gdriveAPI.linkOurFolder(clientId, linkUrl);
      toast.success(`Carpeta "${t.name}" vinculada · ${t.subfolders.length} subcarpetas`);
      setLinkUrl(''); onCreated?.(t);
    } catch (e) { toast.error(e?.message || 'No se pudo vincular'); }
    finally { setBusy(false); }
  };

  return (
    <Card className="border-emerald-300" data-testid="our-drive-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-base"><Building2 className="w-5 h-5 text-emerald-600" /> Carpeta de Karma Digital <Badge variant="outline" className="border-emerald-300 text-emerald-700">Nuestro Drive · interna</Badge></CardTitle>
            <p className="text-xs text-muted-foreground mt-1">Para clientes nuevos se crea sola al darlos de alta. Para los que ya existen, pega el enlace de su carpeta. El cliente no la ve.</p>
          </div>
          {isAdmin && status && (
            <div className="flex items-center gap-2 flex-wrap">
              {status.connected
                ? <span className="text-[11px] text-emerald-700" data-testid="gdrive-connected">Conectado: {status.email}</span>
                : <Button size="sm" variant="outline" onClick={connect} data-testid="gdrive-connect-btn"><Plug className="w-4 h-4 mr-1" /> Conectar Google Drive</Button>}
              <Button size="sm" variant="ghost" onClick={() => setCfgOpen(true)} title="Configurar carpeta general y subcarpetas" data-testid="gdrive-config-btn"><Settings className="w-4 h-4" /></Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {ourDrive?.folder_id ? (
          <>
            <a href={ourDrive.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-3 rounded-lg border hover:border-emerald-500 hover:bg-emerald-50 transition-colors" data-testid="our-drive-main-link">
              <FolderPlus className="w-5 h-5 text-emerald-600" /><span className="font-semibold text-sm flex-1">Carpeta del cliente</span><ExternalLink className="w-4 h-4 text-muted-foreground" />
            </a>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {(ourDrive.subfolders || []).map((sf) => (
                <a key={sf.id} href={sf.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 rounded-md border text-sm hover:border-emerald-500 hover:bg-emerald-50 transition-colors" data-testid={`our-drive-sub-${sf.name}`}>
                  <FolderPlus className="w-4 h-4 text-emerald-600 shrink-0" /><span className="truncate flex-1">{sf.name}</span><ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
                </a>
              ))}
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="our-drive-empty">{status?.connected ? 'Este cliente aún no tiene carpeta en nuestro Drive.' : 'Conecta Google Drive para crear las carpetas automáticamente.'}</p>
        )}
        {isAdmin && status?.connected && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder={ourDrive?.folder_id ? 'Cambiar por otra carpeta nuestra…' : 'Pega el enlace de la carpeta que ya tenéis para este cliente'} className="h-9" data-testid="our-drive-link-input" />
              <Button size="sm" className="h-9 shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={linkExisting} disabled={busy || !linkUrl.trim()} data-testid="our-drive-link-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FolderPlus className="w-4 h-4 mr-1" />} Vincular carpeta existente
              </Button>
            </div>
            {!ourDrive?.folder_id && (
              <Button size="sm" variant="outline" onClick={create} disabled={busy} data-testid="our-drive-create-btn">
                Crear carpeta nueva en Drive (solo si aún no existe)
              </Button>
            )}
          </div>
        )}
      </CardContent>
      <ConfigDialog open={cfgOpen} onOpenChange={setCfgOpen} status={status} onSaved={loadStatus} />
    </Card>
  );
}
